import { formatCurrency } from './currency';
import { countKeysWithPrefix, t } from '../i18n';

// Win-message copy for the post-hand celebration modals and the status
// banner — presentation only. This module never touches poker logic: it
// turns "who won" (already decided by pokerLogic.js and broadcast via
// gameState.winnerInfo) into a personality-driven sentence based on the
// room's Table Vibe setting, optional winner→player "callout" lines, and
// the one-line "Hand over • …" status tail.
//
// Determinism: every client must render the SAME win line for a hand, so
// getWinMessage picks from the pool using winnerInfo.ts as a seed instead of
// Math.random(). Callouts are picked randomly ONCE on the winner's device and
// synced to everyone as {mode, index} — clients render the text locally, so
// raw message text is never stored in Firebase.

export const TABLE_VIBES = {
  CLASSIC: 'classic',
  HYPE: 'hype',
  TRASH_TALK: 'trashTalk',
};

export const CALLOUT_MODES = {
  OFF: 'off',
  FRIENDLY: 'friendly',
  ADULT: 'adult',
};

export const DEFAULT_TABLE_VIBE = TABLE_VIBES.CLASSIC;
export const DEFAULT_CALLOUT_MODE = CALLOUT_MODES.FRIENDLY;

export const TABLE_VIBE_LABELS = {
  get [TABLE_VIBES.CLASSIC]() { return t('settings.vibe.classic'); },
  get [TABLE_VIBES.HYPE]() { return t('settings.vibe.hype'); },
  get [TABLE_VIBES.TRASH_TALK]() { return t('settings.vibe.trashTalk'); },
};

export const CALLOUT_LABELS = {
  get [CALLOUT_MODES.OFF]() { return t('settings.callout.off'); },
  get [CALLOUT_MODES.FRIENDLY]() { return t('settings.callout.friendly'); },
  get [CALLOUT_MODES.ADULT]() { return t('settings.callout.adult'); },
};

export const TABLE_VIBE_EXAMPLES = {
  get [TABLE_VIBES.CLASSIC]() { return t('settings.vibeExample.classic'); },
  get [TABLE_VIBES.HYPE]() { return t('settings.vibeExample.hype'); },
  get [TABLE_VIBES.TRASH_TALK]() { return t('settings.vibeExample.trashTalk'); },
};

export const CALLOUT_EXAMPLES = {
  get [CALLOUT_MODES.FRIENDLY]() { return t('settings.calloutExample.friendly'); },
  get [CALLOUT_MODES.ADULT]() { return t('settings.calloutExample.adult'); },
};

const FALLBACK_NAME = () => t('common.player');

const WIN_MESSAGES = {
  [TABLE_VIBES.CLASSIC]: {
    single: [
      '{winner} takes the pot.',
      '{winner} wins the hand.',
      '{winner} pulls it in.',
      '{winner} collects the pot.',
      '{winner} takes it down.',
      '{winner} wins this one.',
      '{winner} claims the pot.',
      '{winner} gets paid.',
      '{winner} drags the pot.',
      '{winner} wins the round.',
    ],
    split: [
      '{winners} split the pot.',
      '{winners} chop it up.',
      '{winners} share the pot.',
      '{winners} split this one.',
    ],
  },
  [TABLE_VIBES.HYPE]: {
    single: [
      '{winner} just cleaned up.',
      '{winner} is heating up.',
      '{winner} takes it down.',
      '{winner} bags the pot.',
      '{winner} just owned that hand.',
      '{winner} came to play.',
      '{winner} is running the table.',
      '{winner} just picked up a nice one.',
      '{winner} pulls in a big one.',
      '{winner} stays dangerous.',
      '{winner} is building a stack.',
      '{winner} just made a statement.',
    ],
    split: [
      '{winners} chop it up.',
      '{winners} both get paid.',
      '{winners} split the win.',
      '{winners} share the spotlight.',
    ],
  },
  [TABLE_VIBES.TRASH_TALK]: {
    single: [
      '{winner} just robbed the table legally.',
      '{winner} takes the pot. Everyone else, reflect.',
      '{winner} left no crumbs.',
      '{winner} just made that look personal.',
      '{winner} wins. The table is in shambles.',
      '{winner} just collected donations.',
      '{winner} takes it. Tough scene for everyone else.',
      '{winner} just taught a short lesson.',
      '{winner} wins. Please check on the table.',
      '{winner} just turned confidence into chips.',
      '{winner} takes the pot. No further questions.',
      '{winner} just did something rude but legal.',
      '{winner} wins. The rest of you may continue coping.',
      '{winner} just made the table pay rent.',
      '{winner} takes it. That one had consequences.',
      '{winner} did a business.',
    ],
    split: [
      "{winners} split it. Nobody gets to brag too hard.",
      '{winners} chop the pot. Mildly unsatisfying.',
      '{winners} split it. Peace has been restored.',
      '{winners} both survive the chaos.',
    ],
  },
};

const CALLOUT_MESSAGES = {
  [CALLOUT_MODES.FRIENDLY]: [
    '{target}, that one was for you.',
    '{target}, please stay seated.',
    '{target}, you may want to rethink that one.',
    '{target}, tough hand to be involved in.',
    '{target}, thanks for the contribution.',
    '{target}, your stack has been contacted.',
    '{target}, that was personal.',
    '{target}, nothing personal. Probably.',
    '{target}, consider this a learning moment.',
    '{target}, hold that.',
    '{target}, you just got cooked.',
    '{target}, thanks for playing.',
    '{target}, you just made a donation.',
    '{target}, that was embarrassing but legal.',
    "{target}, please blink if you're okay.",
    '{target}, he owns you for the next hand.',
    '{target}, your chips have been repossessed.',
    '{target}, sit there and think about it.',
    '{target}, you got folded emotionally.',
    '{target}, this is why we check.',
    '{target}, wrong table, wrong hand.',
    '{target}, that one had your name on it.',
    '{target}, the table saw that.',
    '{target}, that was a statement.',
  ],
  [CALLOUT_MODES.ADULT]: [
    'Fuck you, {target}. Respectfully.',
    '{target}, get fucked.',
    '{target}, hold that.',
    '{target}, you just got cooked.',
    '{target}, thanks for the donation.',
    '{target}, that was a crime scene.',
    '{target}, sit there and think about it.',
    '{target}, your chips have been repossessed.',
    '{target}, absolutely brutal for you.',
    '{target}, tough scene.',
    '{target}, you just made a donation.',
    '{target}, that was embarrassing as hell.',
    '{target}, please recover privately.',
    '{target}, that was personal.',
    '{target}, you got cooked and served.',
    '{target}, what the hell was that?',
    '{target}, your stack filed a complaint.',
    '{target}, that one should hurt.',
    '{target}, take a walk.',
    '{target}, respectfully, never do that again.',
    "{target}, I'm CEO, Bitch.",
    "{target}, I'm gonna leave you Chipless, bitch.",
  ],
};

/**
 * Join winner names naturally: "Michael", "Michael and Anthony",
 * "Michael, Anthony, and Jake". Falls back to "Player".
 * @param {string[]} names - Winner display names
 * @returns {string} Joined names
 */
export function joinWinnerNames(names) {
  const fallback = FALLBACK_NAME();
  const clean = (names || []).map(n => n || fallback);
  if (clean.length === 0) return fallback;
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return t('names.and', { a: clean[0], b: clean[1] });
  return t('names.commaAnd', { list: clean.slice(0, -1).join(', '), last: clean[clean.length - 1] });
}

function playersById(players) {
  if (!players) return {};
  if (Array.isArray(players)) {
    return Object.fromEntries(players.filter(Boolean).map((p) => [p.id, p]));
  }
  return players;
}

/**
 * Status-banner tail after a hand is awarded. Uses the amounts already
 * written to winnerInfo.winnerWinnings (the awarded cents, including a
 * silent uncalled return folded into a fold-win headline).
 *
 *   one winner     → "Alex wins $5.00"
 *   even / odd-chip split (max−min ≤ 1¢) → "Alex and Sam split $20.00"
 *   different pots → "Alex wins $30.00, Sam wins $20.00"
 *
 * @param {object} winnerInfo
 * @param {object|array} players - Firebase players map or array
 * @param {function} [formatMoney] - cents → display string (room currency)
 * @returns {string} Empty when there are no winner ids
 */
export function handOverStatusTail(winnerInfo, players, formatMoney) {
  const ids = Array.isArray(winnerInfo?.winnerIds)
    ? winnerInfo.winnerIds.filter(Boolean)
    : [];
  if (ids.length === 0) return '';

  const winnings = winnerInfo.winnerWinnings || {};
  const byId = playersById(players);
  const money = typeof formatMoney === 'function'
    ? formatMoney
    : (cents) => formatCurrency(cents);

  const rows = ids.map((id) => ({
    name: byId[id]?.name || FALLBACK_NAME(),
    amount: Math.round(Number(winnings[id]) || 0),
  }));

  if (rows.length === 1) {
    return t('handOver.wins', { name: rows[0].name, amount: money(rows[0].amount) });
  }

  const amounts = rows.map((r) => r.amount);
  const min = Math.min(...amounts);
  const max = Math.max(...amounts);
  const total = amounts.reduce((sum, n) => sum + n, 0);

  // Odd leftover chips from awardPotsToWinners differ by at most 1 cent.
  // That is still a split of the pot, not a side-pot headline.
  if (max - min <= 1) {
    return t('handOver.split', { names: joinWinnerNames(rows.map((r) => r.name)), amount: money(total) });
  }

  return rows.map((r) => t('handOver.wins', { name: r.name, amount: money(r.amount) })).join(', ');
}

/**
 * Get the Table Vibe win line for a hand. Deterministic: the same seed
 * (winnerInfo.ts) yields the same message on every client.
 * @param {string} vibe - Table Vibe setting ('classic' | 'hype' | 'trashTalk'); falls back to Classic
 * @param {string[]} winnerNames - Winner display names (1+ entries)
 * @param {number} seed - Stable per-hand seed shared by all clients (winnerInfo.ts)
 * @returns {string} Fully-rendered win message
 */
export function getWinMessage(vibe, winnerNames, seed) {
  const vibeKey = WIN_MESSAGES[vibe] ? vibe : DEFAULT_TABLE_VIBE;
  const isSplit = (winnerNames || []).length > 1;
  const prefix = isSplit ? `win.${vibeKey}.split` : `win.${vibeKey}.single`;
  const count = countKeysWithPrefix(prefix) || 1;
  const index = Math.abs(Math.floor(Number(seed) || 0)) % count;
  const joined = joinWinnerNames(winnerNames);
  return t(`${prefix}.${index}`, { winner: joined, winners: joined });
}

/**
 * Pick a random callout message index for the given mode (run once on the
 * winner's device; the index — not the text — is synced to Firebase).
 * @param {string} mode - Callout mode ('friendly' | 'adult')
 * @returns {number} Index into the mode's message pool (0 if mode unknown)
 */
export function pickCalloutIndex(mode) {
  const count = countKeysWithPrefix(`callout.${mode}`);
  if (!count) return 0;
  return Math.floor(Math.random() * count);
}

/**
 * Render a synced callout ({mode, index}) into display text on any client.
 * @param {string} mode - Callout mode ('friendly' | 'adult')
 * @param {number} index - Message index picked on the winner's device
 * @param {string} targetName - Target player's display name
 * @returns {string|null} Rendered callout line, or null if mode is off/unknown
 */
export function getCalloutText(mode, index, targetName) {
  const count = countKeysWithPrefix(`callout.${mode}`);
  if (!count) return null;
  const safeIndex = Number.isInteger(index) && index >= 0 && index < count ? index : 0;
  return t(`callout.${mode}.${safeIndex}`, { target: targetName || FALLBACK_NAME() });
}
